import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useOutletContext } from "react-router-dom";
import { meApi } from "../../api/me";
import type { UserOut } from "../../api/types";
import { errorMessage } from "../../shared/errors";
import { useInvalidateMe } from "../../shared/hooks/useMe";
import { Alert, Badge, Button, Card, Input } from "../../ui";

export function OrganizationsPage() {
  const me = useOutletContext<UserOut>();
  const queryClient = useQueryClient();
  const invalidateMe = useInvalidateMe();
  const orgs = useQuery({ queryKey: ["me", "orgs"], queryFn: meApi.orgs });
  const [name, setName] = useState("");
  const [member, setMember] = useState({
    orgId: "",
    email: "",
    role: "member",
  });
  const [error, setError] = useState<string | null>(null);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["me"] });
    await invalidateMe();
  };
  const create = useMutation({
    mutationFn: () => meApi.createOrg(name),
    onSuccess: () => {
      setName("");
      return refresh();
    },
    onError: (err) => setError(errorMessage(err)),
  });
  const activate = useMutation({
    mutationFn: (id: string) => meApi.setActiveOrg(id),
    onSuccess: refresh,
  });
  const addMember = useMutation({
    mutationFn: () =>
      meApi.addMember(member.orgId, { email: member.email, role: member.role }),
    onSuccess: () => {
      setMember({ orgId: "", email: "", role: "member" });
      setError(null);
      return refresh();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <h1 className="mb-4 text-xl font-semibold">Organizations</h1>
        {error ? <Alert kind="error">{error}</Alert> : null}
        <ul className="divide-y divide-slate-100 text-sm">
          {(orgs.data ?? []).map((o) => (
            <li key={o.id} className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <span className="font-medium">{o.name}</span>
                <Badge>{o.kind}</Badge>
                <Badge tone="neutral">{o.role}</Badge>
                {me.active_org_id === o.id ? (
                  <Badge tone="success">active</Badge>
                ) : null}
              </div>
              <div className="flex gap-2">
                {me.active_org_id !== o.id ? (
                  <Button
                    variant="secondary"
                    onClick={() => activate.mutate(o.id)}
                  >
                    Use
                  </Button>
                ) : null}
                {o.kind === "business" &&
                (o.role === "owner" || o.role === "admin") ? (
                  <Button
                    variant="secondary"
                    onClick={() => setMember({ ...member, orgId: o.id })}
                  >
                    Add member
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </Card>
      <Card>
        <h2 className="mb-4 text-lg font-semibold">New organization</h2>
        <form
          className="flex max-w-md items-end gap-2"
          aria-label="Create organization"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Input
            label="Name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Button type="submit" loading={create.isPending}>
            Create
          </Button>
        </form>
      </Card>
      {member.orgId ? (
        <Card>
          <h2 className="mb-4 text-lg font-semibold">Add member</h2>
          <form
            className="flex max-w-lg flex-col gap-3"
            aria-label="Add member"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              addMember.mutate();
            }}
          >
            <Input
              label="Email of an existing Intelibo account"
              type="email"
              required
              value={member.email}
              onChange={(e) => setMember({ ...member, email: e.target.value })}
            />
            <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
              Role
              <select
                className="rounded-md border border-slate-300 px-3 py-2 text-sm font-normal"
                value={member.role}
                onChange={(e) => setMember({ ...member, role: e.target.value })}
              >
                <option value="member">member</option>
                <option value="admin">admin</option>
                <option value="owner">owner</option>
              </select>
            </label>
            <div className="flex gap-2">
              <Button type="submit" loading={addMember.isPending}>
                Add
              </Button>
              <Button
                variant="secondary"
                type="button"
                onClick={() =>
                  setMember({ orgId: "", email: "", role: "member" })
                }
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      ) : null}
    </div>
  );
}
